import api, { setAccessToken, getAccessToken } from './api';

export type UserRole = 'ADMIN' | 'MANAGER' | 'SELLER';

export interface UserProduct {
  productId: number;
  product: {
    id: number;
    name: string;
    slug: string;
  };
}

export interface User {
  id: number;
  email: string;
  name: string;
  role: UserRole;
  active: boolean;
  mustChangePassword?: boolean;
  twoFactorEnabled?: boolean;
  products?: UserProduct[];
}

export interface LoginTokens {
  accessToken: string;
  refreshToken?: string;
}

export interface AuthResponse {
  user: User;
  tokens?: LoginTokens;
  accessToken?: string;
  requiresTwoFactor?: boolean;
}

export const authService = {
  async login(email: string, password: string): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>('/auth/login', {
      email,
      password,
    });

    // Only store token if not requiring 2FA
    const token = response.data.tokens?.accessToken ?? response.data.accessToken;
    if (token) {
      setAccessToken(token);
    }

    return response.data;
  },

  async verifyTwoFactor(email: string, token: string): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>('/auth/2fa/verify', {
      email,
      token,
    });

    const accessToken = response.data.tokens?.accessToken ?? response.data.accessToken;
    if (accessToken) {
      setAccessToken(accessToken);
    }

    return response.data;
  },

  async logout(): Promise<void> {
    // Cookie is sent automatically via withCredentials
    await api.post('/auth/logout');
    setAccessToken(null);
  },

  async getCurrentUser(): Promise<User> {
    const response = await api.get<User>('/auth/me');
    return response.data;
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await api.post('/auth/change-password', {
      currentPassword,
      newPassword,
    });
  },

  getStoredAccessToken(): string | null {
    return getAccessToken();
  },

  setAccessToken(token: string): void {
    setAccessToken(token);
  },

  clearTokens(): void {
    setAccessToken(null);
  },
};

export default authService;
